import type { ChainId } from '$lib/models'

const DEFAULT_COMPETITORS: ChainId[] = [
	'ethereum_chain',
	'bitcoin_chain',
	'bnb_chain',
	'solana_chain',
	'cardano_chain',
	'avalanche_chain',
	'polkadot_ecosystem_chain',
	'polygon_chain',
	'fantom_chain',
	'arbitrum_chain',
]

const POLKADOT_PARACHAINS_COMPETITORS: ChainId[] = [
	'acala_chain',
	'ajuna_chain',
	'astar_chain',
	'bifrost_chain',
	'crust_chain',
	'frequency_chain',
	'hydration_chain',
	'integritee_chain',
	'litentry_chain',
	'mythos_chain',
	'moonbeam_chain',
	'neuroweb_chain',
	'peaq_chain',
	'pendulum_chain',
	'phala_chain',
	'robonomics_chain',
	'polkadot_ecosystem_chain',
	'zeitgeist_chain',
]

/* eslint sort-keys: ["error", "asc", { caseSensitive: true, natural: true }] */
export const CHAINS_COMPETITORS: Record<ChainId, ChainId[]> = {
	acala_chain: POLKADOT_PARACHAINS_COMPETITORS,
	aelf_chain: DEFAULT_COMPETITORS,
	aeternity_chain: DEFAULT_COMPETITORS,
	airdao_chain: DEFAULT_COMPETITORS,
	ajuna_chain: POLKADOT_PARACHAINS_COMPETITORS,
	aleph_zero_chain: DEFAULT_COMPETITORS,
	alephium_chain: DEFAULT_COMPETITORS,
	algorand_chain: [...DEFAULT_COMPETITORS, 'hedera_chain'],
	aptos_chain: DEFAULT_COMPETITORS,
	arbitrum_chain: ['optimism_chain', ...DEFAULT_COMPETITORS],
	areon_chain: DEFAULT_COMPETITORS,
	arthera_chain: DEFAULT_COMPETITORS,
	astar_chain: POLKADOT_PARACHAINS_COMPETITORS,
	aura_chain: DEFAULT_COMPETITORS,
	aurora_chain: DEFAULT_COMPETITORS,
	autonomys_chain: DEFAULT_COMPETITORS,
	avalanche_chain: [...DEFAULT_COMPETITORS.filter(id => id !== 'avalanche_chain'), 'hedera_chain'],
	base_chain: [...DEFAULT_COMPETITORS, 'optimism_chain'],
	berachain_chain: DEFAULT_COMPETITORS,
	bifrost_chain: POLKADOT_PARACHAINS_COMPETITORS,
	bitcoin_chain: [...DEFAULT_COMPETITORS.filter(id => id !== 'bitcoin_chain'), 'hedera_chain'],
	bitgert_chain: DEFAULT_COMPETITORS,
	blast_chain: DEFAULT_COMPETITORS,
	blockdag_chain: DEFAULT_COMPETITORS,
	bnb_chain: [...DEFAULT_COMPETITORS.filter(id => id !== 'bnb_chain'), 'hedera_chain'],
	bnb_greenfield_chain: ['bnb_chain', 'opbnb_chain'],
	bsv_chain: DEFAULT_COMPETITORS,
	cardano_chain: [...DEFAULT_COMPETITORS.filter(id => id !== 'cardano_chain'), 'hedera_chain'],
	celestia_chain: DEFAULT_COMPETITORS,
	celo_chain: DEFAULT_COMPETITORS,
	chainlink_chain: DEFAULT_COMPETITORS,
	chromia_chain: DEFAULT_COMPETITORS,
	concordium_chain: DEFAULT_COMPETITORS,
	core_chain: DEFAULT_COMPETITORS,
	cosmos_chain: DEFAULT_COMPETITORS,
	cronos_chain: [...DEFAULT_COMPETITORS, 'algorand_chain'],
	crust_chain: ['polkadot_ecosystem_chain', 'integritee_chain', 'zeitgeist_chain'],
	ecash_chain: DEFAULT_COMPETITORS,
	electra_protocol_chain: DEFAULT_COMPETITORS,
	electroneum_chain: DEFAULT_COMPETITORS,
	eos_chain: DEFAULT_COMPETITORS,
	ethereum_chain: [...DEFAULT_COMPETITORS.filter(id => id !== 'ethereum_chain'), 'hedera_chain'],
	etherlink_chain: DEFAULT_COMPETITORS,
	fantom_chain: [...DEFAULT_COMPETITORS.filter(id => id !== 'fantom_chain'), 'hedera_chain'],
	flow_chain: DEFAULT_COMPETITORS,
	fogo_chain: DEFAULT_COMPETITORS,
	frequency_chain: POLKADOT_PARACHAINS_COMPETITORS,
	gnosis_chain: DEFAULT_COMPETITORS,
	gravity_chain: DEFAULT_COMPETITORS,
	haven1_chain: DEFAULT_COMPETITORS,
	hedera_chain: DEFAULT_COMPETITORS,
	hive_chain: DEFAULT_COMPETITORS,
	hydration_chain: POLKADOT_PARACHAINS_COMPETITORS,
	hyperliquid_chain: DEFAULT_COMPETITORS,
	icp_chain: DEFAULT_COMPETITORS,
	integritee_chain: ['polkadot_ecosystem_chain', 'crust_chain', 'zeitgeist_chain'],
	iota_chain: DEFAULT_COMPETITORS,
	iotex_chain: DEFAULT_COMPETITORS,
	kaspa_chain: DEFAULT_COMPETITORS,
	kava_chain: DEFAULT_COMPETITORS,
	keeta_chain: DEFAULT_COMPETITORS,
	klaytn_chain: DEFAULT_COMPETITORS,
	klever_chain: DEFAULT_COMPETITORS,
	kusama_chain: DEFAULT_COMPETITORS,
	linea_chain: DEFAULT_COMPETITORS,
	litentry_chain: POLKADOT_PARACHAINS_COMPETITORS,
	mantle_chain: DEFAULT_COMPETITORS,
	massa_chain: DEFAULT_COMPETITORS,
	matchain_chain: DEFAULT_COMPETITORS,
	mazze_chain: DEFAULT_COMPETITORS,
	megaeth_chain: DEFAULT_COMPETITORS,
	monad_chain: DEFAULT_COMPETITORS,
	moonbeam_chain: [
		'polkadot_ecosystem_chain',
		'crust_chain',
		'integritee_chain',
		'polkadex_chain',
		'zeitgeist_chain',
	],
	multiversx_chain: DEFAULT_COMPETITORS,
	mythos_chain: POLKADOT_PARACHAINS_COMPETITORS,
	near_chain: DEFAULT_COMPETITORS,
	neuroweb_chain: POLKADOT_PARACHAINS_COMPETITORS,
	nexa_chain: DEFAULT_COMPETITORS,
	opbnb_chain: ['bnb_chain', 'bnb_greenfield_chain'],
	optimism_chain: [...DEFAULT_COMPETITORS, 'hedera_chain', 'cronos_chain'],
	particle_chain: DEFAULT_COMPETITORS,
	peaq_chain: POLKADOT_PARACHAINS_COMPETITORS,
	pendulum_chain: POLKADOT_PARACHAINS_COMPETITORS,
	phala_chain: POLKADOT_PARACHAINS_COMPETITORS,
	polkadex_chain: [
		'polkadot_ecosystem_chain',
		'crust_chain',
		'integritee_chain',
		'zeitgeist_chain',
	],
	polkadot_ecosystem_chain: [
		...DEFAULT_COMPETITORS,
		'crust_chain',
		'integritee_chain',
		'polkadex_chain',
		'zeitgeist_chain',
	],
	polygon_chain: [...DEFAULT_COMPETITORS.filter(id => id !== 'polygon_chain'), 'hedera_chain'],
	quai_chain: DEFAULT_COMPETITORS,
	qubic_chain: DEFAULT_COMPETITORS,
	radix_chain: DEFAULT_COMPETITORS,
	redbelly_chain: DEFAULT_COMPETITORS,
	reddio_chain: DEFAULT_COMPETITORS,
	rise_chain: DEFAULT_COMPETITORS,
	robonomics_chain: POLKADOT_PARACHAINS_COMPETITORS,
	scroll_chain: DEFAULT_COMPETITORS,
	sei_chain: DEFAULT_COMPETITORS,
	sirius_chain: DEFAULT_COMPETITORS,
	skale_chain: DEFAULT_COMPETITORS,
	solana_chain: [
		...DEFAULT_COMPETITORS.filter(id => id !== 'solana_chain' && id !== 'arbitrum_chain'),
		'hedera_chain',
		'algorand_chain',
	],
	somnia_chain: DEFAULT_COMPETITORS,
	sonic_chain: DEFAULT_COMPETITORS,
	starknet_chain: ['arbitrum_chain', 'polygon_chain', 'solana_chain'],
	stellar_chain: DEFAULT_COMPETITORS,
	sui_chain: DEFAULT_COMPETITORS,
	supra_chain: DEFAULT_COMPETITORS,
	taraxa_chain: DEFAULT_COMPETITORS,
	tectum_chain: DEFAULT_COMPETITORS,
	telos_chain: ['solana_chain', 'ethereum_chain', 'polkadot_ecosystem_chain'],
	terra_chain: DEFAULT_COMPETITORS,
	tezos_chain: DEFAULT_COMPETITORS,
	ton_chain: DEFAULT_COMPETITORS,
	tron_chain: DEFAULT_COMPETITORS,
	u2u_chain: DEFAULT_COMPETITORS,
	unichain_chain: DEFAULT_COMPETITORS,
	vechain_chain: DEFAULT_COMPETITORS,
	wanchain_chain: ['polkadot_ecosystem_chain'],
	waterfall_chain: DEFAULT_COMPETITORS,
	xelis_chain: DEFAULT_COMPETITORS,
	xrpl_chain: DEFAULT_COMPETITORS,
	xx_chain: DEFAULT_COMPETITORS,
	zeitgeist_chain: [
		'polkadot_ecosystem_chain',
		'crust_chain',
		'integritee_chain',
		'polkadex_chain',
	],
}
